import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { RT_LIST, AGAMA, PERKAWINAN, SHDK, PENDIDIKAN, PEKERJAAN, GOLDAR, DISABILITAS, EMPTY_WARGA } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FieldSelect } from "@/components/FieldSelect";

const F = ({ label, children, hint }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-[11px] mt-1 text-slate-500">{hint}</p>}
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-xs font-bold tracking-widest uppercase text-amber-700 mb-3">{title}</div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  </div>
);

export const WargaForm = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY_WARGA);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial?.id);

  useEffect(() => {
    if (open) setF(initial ? { ...EMPTY_WARGA, ...initial } : EMPTY_WARGA);
  }, [open, initial]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const digits = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value.replace(/\D/g, "").slice(0, 16) }));
  const txt = (k, testid, props = {}) => <Input value={f[k] ?? ""} onChange={set(k)} data-testid={testid} className="bg-white" {...props} />;
  const sel = (k, options, testid) => <FieldSelect value={f[k]} onChange={set(k)} options={options} testid={testid} />;

  const submit = async (e) => {
    e.preventDefault();
    const required = ["nik", "no_kk", "nama", "jenis_kelamin", "tempat_lahir", "tanggal_lahir", "agama", "status_perkawinan", "shdk", "pendidikan", "pekerjaan", "status_oap", "alamat", "rt"];
    const missing = required.filter((k) => !f[k]);
    if (missing.length) return toast.error("Lengkapi semua kolom bertanda *");
    if (f.nik.length !== 16 || f.no_kk.length !== 16) return toast.error("NIK dan No. KK wajib 16 digit");
    setSaving(true);
    try {
      const body = { ...f, rt: Number(f.rt), penghasilan: Number(f.penghasilan || 0) };
      ["id", "status", "usia", "kelompok_usia", "status_ekonomi", "pendapatan_per_kapita", "created_at", "updated_at", "created_by"].forEach((k) => delete body[k]);
      if (editing) await api.put(`/warga/${initial.id}`, body);
      else await api.post("/warga", body);
      toast.success(editing ? "Data warga diperbarui" : "Data warga berhasil disimpan");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-[#fbfaf7]" data-testid="warga-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Ubah Data Warga" : "Pendataan Warga Baru"}</DialogTitle>
          <DialogDescription>Format sesuai biodata kependudukan Dukcapil. Kolom bertanda * wajib diisi.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-7 mt-2">
          <Section title="Identitas">
            <F label="NIK *" hint={`${f.nik.length}/16 digit`}>{txt("nik", "form-nik", { onChange: digits("nik"), inputMode: "numeric", className: "bg-white font-mono" })}</F>
            <F label="Nomor KK *" hint={`${f.no_kk.length}/16 digit`}>{txt("no_kk", "form-no-kk", { onChange: digits("no_kk"), inputMode: "numeric", className: "bg-white font-mono" })}</F>
            <F label="Nama Lengkap *">{txt("nama", "form-nama")}</F>
            <F label="Jenis Kelamin *">{sel("jenis_kelamin", ["Laki-laki", "Perempuan"], "form-jk")}</F>
            <F label="Tempat Lahir *">{txt("tempat_lahir", "form-tempat-lahir")}</F>
            <F label="Tanggal Lahir *">{txt("tanggal_lahir", "form-tanggal-lahir", { type: "date" })}</F>
            <F label="Golongan Darah">{sel("golongan_darah", GOLDAR, "form-goldar")}</F>
            <F label="Agama *">{sel("agama", AGAMA, "form-agama")}</F>
            <F label="Status Perkawinan *">{sel("status_perkawinan", PERKAWINAN, "form-perkawinan")}</F>
          </Section>
          <Section title="Keluarga & Asal">
            <F label="Hubungan dalam Keluarga *">{sel("shdk", SHDK, "form-shdk")}</F>
            <F label="Nama Ayah">{txt("nama_ayah", "form-nama-ayah")}</F>
            <F label="Nama Ibu">{txt("nama_ibu", "form-nama-ibu")}</F>
            <F label="Kewarganegaraan *">{sel("kewarganegaraan", ["WNI", "WNA"], "form-kewarganegaraan")}</F>
            <F label="Status Asli Papua *">{sel("status_oap", [{ value: "OAP", label: "OAP (Orang Asli Papua)" }, { value: "Non-OAP", label: "Non-OAP (Pendatang)" }], "form-oap")}</F>
            <F label="Suku">{txt("suku", "form-suku", { placeholder: "mis. Kamoro, Amungme" })}</F>
          </Section>
          <Section title="Pendidikan, Pekerjaan & Ekonomi">
            <F label="Pendidikan Terakhir *">{sel("pendidikan", PENDIDIKAN, "form-pendidikan")}</F>
            <F label="Pekerjaan *">{sel("pekerjaan", PEKERJAAN, "form-pekerjaan")}</F>
            <F label="Penghasilan per Bulan (Rp)" hint="Isi 0 jika tidak berpenghasilan">{txt("penghasilan", "form-penghasilan", { type: "number", min: 0 })}</F>
            <F label="Disabilitas">{sel("disabilitas", DISABILITAS, "form-disabilitas")}</F>
            <F label="No. HP">{txt("no_hp", "form-no-hp", { inputMode: "tel" })}</F>
          </Section>
          <Section title="Alamat & Pendataan">
            <F label="RT *">{sel("rt", RT_LIST.map((n) => ({ value: n, label: `RT ${String(n).padStart(2, "0")}` })), "form-rt")}</F>
            <F label="RW">{txt("rw", "form-rw")}</F>
            <F label="Alamat Lengkap *">{txt("alamat", "form-alamat")}</F>
            {!editing && (
              <F label="Keterangan Pendataan" hint="Lahir/Datang otomatis tercatat di Mutasi">{sel("keterangan_masuk", ["Penduduk Tetap", "Lahir", "Datang"], "form-keterangan-masuk")}</F>
            )}
          </Section>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel-button">Batal</Button>
            <Button type="submit" disabled={saving} className="bg-[#0b4f4a] hover:bg-[#072f2c]" data-testid="form-save-button">
              {saving ? "Menyimpan…" : "Simpan Data"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
